/**
 * i18n.jsx — Check a Payment internationalisation
 * ---------------------------------------------------------------
 * Deliberately small: a context, a persisted selection, and a
 * fallback-to-English lookup. See `copy.js` for why every non-English
 * dictionary is currently empty rather than machine-translated.
 *
 * FEATURES.md "Internationalisation" requirements this satisfies:
 *   - English canonical/default                         (DEFAULT_LANGUAGE)
 *   - user selection overrides browser detection, and is remembered
 *     (localStorage, read once on mount; browser locale is never
 *     auto-applied — see note below)
 *   - architecture allows more languages / RTL later without
 *     redesign (add a row to LANGUAGES + a dictionary in copy.js;
 *     `dir` is already threaded through for a future RTL language)
 *
 * We deliberately do NOT auto-select a language from the browser's
 * locale: every non-English dictionary is untranslated today, so
 * silently switching a French-locale visitor to a "French" mode that
 * renders English text with no signal would be more confusing than
 * defaulting everyone to the explicit, reviewed English content.
 * The selector is still fully populated so the *mechanism* (pick a
 * language, have it stick) is real and testable end to end.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { COPY, LANGUAGES, DEFAULT_LANGUAGE } from "./copy.js";

const STORAGE_KEY = "askmax_pc_lang";
const RTL_LANGUAGES = new Set(); // none yet — placeholder for future RTL languages

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(DEFAULT_LANGUAGE);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved && LANGUAGES.some((l) => l.code === saved)) {
        setLangState(saved);
      }
    } catch {
      // localStorage unavailable (private browsing, etc) — fall back
      // to the default language rather than failing.
    }
  }, []);

  const setLang = (code) => {
    if (!LANGUAGES.some((l) => l.code === code)) return;
    setLangState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // Non-fatal: the selection just won't survive a reload.
    }
  };

  const meta = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const value = useMemo(
    () => ({
      lang,
      setLang,
      languages: LANGUAGES,
      isReviewed: meta.reviewed,
      dir: RTL_LANGUAGES.has(lang) ? "rtl" : "ltr",
    }),
    [lang, meta.reviewed]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

function useLanguageContext() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage/useCopy must be used within LanguageProvider");
  return ctx;
}

export function useLanguage() {
  return useLanguageContext();
}

/** Returns a `t(key)` lookup function bound to the active language. */
export function useCopy() {
  const { lang } = useLanguageContext();
  return useMemo(() => {
    const dict = COPY[lang] || {};
    const fallback = COPY[DEFAULT_LANGUAGE];
    return (key) => dict[key] ?? fallback[key] ?? key;
  }, [lang]);
}
